import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import SkipToContent from "@/components/SkipToContent";
import ErrorBoundary from "@/components/ErrorBoundary";
import Analytics from "@/components/Analytics";
import RootLayout from "@/components/RootLayout";
import { Suspense, lazy } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useDeviceDetection } from "@/mobile/hooks/useDeviceDetection";

// Lazy load pages
const Index = lazy(() => import("./pages/Index"));
const BeginnersGuide = lazy(() => import("./pages/BeginnersGuide"));
const SportsCardHistory = lazy(() => import("./pages/SportsCardHistory"));
const Glossary = lazy(() => import("./pages/Glossary"));
const TypesOfCards = lazy(() => import("./pages/TypesOfCards"));
const CardValue = lazy(() => import("./pages/CardValue"));
const GradingGuide = lazy(() => import("./pages/GradingGuide"));
const BuySell = lazy(() => import("./pages/BuySell"));
const TrackingInvestments = lazy(() => import("./pages/TrackingInvestments"));
const ScamsMistakes = lazy(() => import("./pages/ScamsMistakes"));
const HotCards = lazy(() => import("./pages/HotCards"));
const SuppliesProtection = lazy(() => import("./pages/SuppliesProtection"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Prizes = lazy(() => import("./pages/Prizes"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SportsCardScene = lazy(() => import("./pages/SportsCardScene"));
const FollowUs = lazy(() => import("./pages/FollowUs"));
const MarketDrivers = lazy(() => import("./pages/MarketDrivers"));
const Education = lazy(() => import("./pages/Education"));
const TheGame = lazy(() => import("./pages/TheGame"));
const Auth = lazy(() => import("./pages/Auth"));
const Community = lazy(() => import("./pages/Community"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const CategoryFeed = lazy(() => import("./pages/CategoryFeed"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const CreatePost = lazy(() => import("./pages/CreatePost"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CompeteNow = lazy(() => import("./pages/CompeteNow"));

const MobileApp = lazy(() => import("./mobile/MobileApp"));

const queryClient = new QueryClient();

const App = () => {
  const { isMobile } = useDeviceDetection();

  return (
    <RootLayout>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Analytics />
            <BrowserRouter>
              <SkipToContent />
              <ScrollToTop />
              <Suspense fallback={
                <div className="flex h-screen w-full items-center justify-center">
                  <LoadingSpinner size="lg" />
                </div>
              }>
                {isMobile ? (
                  <MobileApp />
                ) : (
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/compete-now" element={<CompeteNow />} />
                    <Route path="/the-game" element={<TheGame />} />
                    <Route path="/education" element={<Education />} />
                    <Route path="/beginners-guide" element={<BeginnersGuide />} />
                    <Route path="/history" element={<SportsCardHistory />} />
                    <Route path="/glossary" element={<Glossary />} />
                    <Route path="/types-of-cards" element={<TypesOfCards />} />
                    <Route path="/card-value" element={<CardValue />} />
                    <Route path="/grading-guide" element={<GradingGuide />} />
                    <Route path="/buy-sell" element={<BuySell />} />
                    <Route path="/tracking-investments" element={<TrackingInvestments />} />
                    <Route path="/scams-mistakes" element={<ScamsMistakes />} />
                    <Route path="/hot-cards" element={<HotCards />} />
                    <Route path="/supplies-protection" element={<SuppliesProtection />} />
                    <Route path="/prizes" element={<Prizes />} />

                    <Route path="/auth" element={<Auth />} />
                    <Route path="/u/:username" element={<UserProfile />} />
                    <Route path="/account/settings" element={<AccountSettings />} />

                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/sports-card-scene" element={<SportsCardScene />} />
                    <Route path="/follow-us" element={<FollowUs />} />
                    <Route path="/market-drivers" element={<MarketDrivers />} />
                    {/* Redirects */}
                    <Route path="/history" element={<SportsCardHistory />} />
                    <Route path="/collectors" element={<SportsCardScene />} />
                    <Route path="/promotions" element={<SportsCardScene />} />
                    <Route path="/videos" element={<SportsCardScene />} />
                    <Route path="/community" element={<FollowUs />} />
                    <Route path="/community/guidelines" element={<FollowUs />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                )}
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </RootLayout>
  );
};

export default App;
